/**
 * Mulligan status TanStack Query hook.
 *
 * Returns the player's view of windows, allowances, and locked picks for a
 * pool. The status response is time-sensitive (windows open/close on the
 * server clock), so we use a short staleTime per the design.
 *
 * Requirements: 10.1
 * Design: Frontend Components → TanStack Query Hooks (staleTime 15s)
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';

/**
 * Query key factory for mulligan status.
 */
export const mulliganStatusKeys = {
  all: ['mulligan-status'] as const,
  pool: (poolId: string) => [...mulliganStatusKeys.all, poolId] as const,
} as const;

/**
 * Hook to fetch the player's mulligan status for a pool.
 *
 * `staleTime` is 15 seconds so the open-window indicator and countdown
 * stay close to the server clock without hammering the API.
 */
export function useMulliganStatus(poolId: string) {
  return useQuery({
    queryKey: mulliganStatusKeys.pool(poolId),
    queryFn: async () => {
      const response = await api.mulligan.getMulliganStatus(poolId);
      if (response.error) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: !!poolId,
    staleTime: 1000 * 15, // 15 seconds
  });
}
